import {
  Badge,
  Button,
  Flex,
  FormatDate,
  Icon,
  IconButton,
  Text,
  Tooltip,
  TyreInfoBox,
} from '@wyz/ui';
import {
  DateUtils,
  mapTyreIntoInfoBox,
  OFFER_TYPE,
  useBoolean,
} from '@wyz/utils';
import clsx from 'clsx';
import { DebouncedFunc, isNil } from 'lodash-es';
import { useManageCart, useManageSearch } from '~/purchase/api';
import {
  PurchaseAddToCartButton,
  PurchaseDetailModal,
  PurchaseOfferDescriptionCell,
} from '~/purchase/components';
import { MIN_QUANTITY, PurchaseErrorCodesLookup } from '~/purchase/helpers';

import {
  CUSTOMER_LEVEL,
  IBaseTyreOffer,
  IOfferMetadata,
  ITyreSupplier,
} from '@wyz/types';
import { useFormContext } from 'react-hook-form';
import { NumberField, PrivateComponent } from '~/common/components';
import { Trans, useConfig, useI18n, useLocale } from '~/common/context';
import { FEATURES, PurchaseErrorCodes, ROLES } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { IUserCustomer } from '~/common/types';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import { IOffer } from '~/purchase/types';

type Props = {
  customer: IUserCustomer;
  onUpdateQuantity: (
    supplier: ITyreSupplier,
  ) => DebouncedFunc<(newQuantity?: number | null) => void>;
  isAdding: boolean;
  showCommon: boolean;
  offersCount: number;
  offerIndex: number;
  product: Pick<
    IBaseTyreOffer,
    | 'status'
    | 'totalAvailableQuantity'
    | 'tyre'
    | 'tyreCreatedAt'
    | 'alternativeCount'
    | 'canOrder'
    | 'customerStock'
    | 'containsFallbackOffers'
  >;
  requestedQuantity: number;
  offer: {
    supplier: ITyreSupplier;
    offerMetadata: IOfferMetadata;
  };
  onRefreshTyre: (ean: string) => Promise<Array<IOffer>>;
};

function OfferSuccessRow({
  product,
  isAdding,
  requestedQuantity,
  onUpdateQuantity,
  offer,
  showCommon,
  offersCount,
  offerIndex,
  customer,
  onRefreshTyre,
}: Props) {
  const { t } = useI18n();
  const [detailOpened, detailHandlers] = useBoolean();
  const acl = useAccessControl();
  const { cdnUrl } = useConfig();
  const locale = useLocale();
  const searchContext = {
    customerId: customer.id,
    searchType:
      customer.level === CUSTOMER_LEVEL.N1
        ? SEARCH_TYPE_ENUM.DISTRIBUTION
        : SEARCH_TYPE_ENUM.REDISTRIBUTION,
  };

  const manageSearch = useManageSearch<IBaseTyreOffer>({
    context: searchContext,
    refreshEan: { enabled: true },
  });
  const methods = useFormContext();

  const supplier = offer.supplier;
  const cart = useManageCart({
    context: { customerId: customer.id },
    get: { enabled: true },
    patch: { enabled: true },
    remove: { enabled: true },
  });
  const cartItems =
    cart.get.data?.data.items.filter(
      (item) => item.tyre.ean === product.tyre.ean,
    ) || [];
  const tyreOrderedQuantity = cartItems.reduce(
    (acc, curr) => acc + curr.quantity,
    0,
  );
  const isRedistributionEnabled = acl.isEnabledFeatures([
    FEATURES.REDISTRIBUTION,
  ]);
  const isInRedistribution =
    isRedistributionEnabled && customer.level > CUSTOMER_LEVEL.N1;

  const isPartiallyAvailable =
    product.totalAvailableQuantity > 0 &&
    product.totalAvailableQuantity < requestedQuantity;

  return (
    <tr
      className={clsx('text-nowrap fs-xs', {
        subhead: showCommon,
        main: offerIndex === offersCount - 1,
      })}
    >
      {showCommon && (
        <td id='product' rowSpan={offersCount}>
          <TyreInfoBox
            imageBaseUrl={cdnUrl}
            t={t}
            tyre={mapTyreIntoInfoBox(
              product.tyre,
              customer.settingTemplate?.generalSetting.tyreIdentifierFields ??
                undefined,
            )}
            direction='horizontal'
            mode='FULL'
          />
        </td>
      )}
      {showCommon && (
        <td id='detail' rowSpan={offersCount} className='p-0'>
          <PurchaseOfferDescriptionCell
            product={product}
            onDetail={detailHandlers.setTrue}
          />
        </td>
      )}
      {showCommon && (
        <td
          id='bf-price'
          rowSpan={offersCount}
          className='text-center align-middle'
        >
          <Text heading='xs' weight='semi-bold' id='bf-price-value'>
            {locale.formatters.money(product.tyre.priceFrame.price, {
              currency: product.tyre.priceFrame.currencyIsoCode,
              fallback: '/',
            })}
          </Text>
        </td>
      )}
      {showCommon && customer?.hasInternalStocks && (
        <td
          className='text-center align-middle'
          rowSpan={offersCount}
          id='customer-stock'
        >
          <Text heading='xs' weight='semi-bold'>
            {product.customerStock ?? '/'}
          </Text>
        </td>
      )}
      <td id='supplier' className='p-0 no-border'>
        <Flex
          direction='column'
          justifyContent='around'
          className='list-unstyled h-100 w-100'
          wrap='wrap'
        >
          <div
            className={clsx('w-100 px-1 d-flex align-items-center', {
              'multi': !showCommon,
              'fw-bold text-orange-1': Boolean(offer.offerMetadata.isJ0),
            })}
          >
            <Text
              heading='xs'
              weight='semi-bold'
              color='tertiary'
              className='text-wrap'
              id={`supplier-${product.tyre.ean}-${supplier.name}`}
            >
              <span>{supplier.name}</span>
            </Text>
          </div>
          {customer.level > CUSTOMER_LEVEL.N1 && (
            <div className='m-2'>
              {offer.offerMetadata.offerType ===
              OFFER_TYPE.DISTRIBUTOR_PLATFORM ? (
                <Badge bg='medium-green'>
                  <Text
                    weight='bolder'
                    heading='xs'
                    color='inherit'
                    className='px-2'
                  >
                    <Trans i18nKey='app.purchase.redistribution.in_stock_offer' />
                  </Text>
                </Badge>
              ) : (
                <Badge bg='pumpkin'>
                  <Text
                    weight='bolder'
                    heading='xs'
                    color='inherit'
                    className='px-2'
                  >
                    <Trans i18nKey='app.purchase.redistribution.out_of_stock_offer' />
                  </Text>
                </Badge>
              )}
            </div>
          )}
        </Flex>
      </td>
      <td
        id='delivery'
        className={clsx('p-0 no-border', {
          'bg-tertiary-bold text-white': Boolean(offer.offerMetadata.isJ0),
        })}
      >
        {offer.offerMetadata.isFallback ? (
          <Flex alignItems='center' justifyContent='center' className='gap-2'>
            <Text
              id={`fallback-delivery-${product.tyre.ean}-${supplier.name}-${offerIndex}`}
              weight='semi-bold'
            >
              {'/'}
            </Text>
            {offer.offerMetadata.gspErrors.map((error) => (
              <Tooltip key={error} label={t(PurchaseErrorCodesLookup[error])}>
                <Icon icon='info-circle' color='secondary' />
              </Tooltip>
            ))}
            {offer.offerMetadata.gspErrors.length === 0 && (
              <Tooltip label={t('app.purchase.supplier_timeout')}>
                <Icon icon='info-circle' color='secondary' />
              </Tooltip>
            )}
          </Flex>
        ) : (
          <Flex
            direction='column'
            alignItems='center'
            className='list-unstyled h-100 w-100'
            wrap='wrap'
          >
            <div
              className={clsx(
                'd-flex w-100 px-1 space-x-1 align-items-center justify-content-center gap-2',
                {
                  'justify-content-center': Boolean(!offer.offerMetadata.isJ0),
                  'multi': !showCommon,
                },
              )}
            >
              <>
                <Text
                  heading='xs'
                  weight='semi-bold'
                  id={`delivery-${product.tyre.ean}-${supplier.name}-${offerIndex}`}
                  color={offer.offerMetadata.isJ0 ? 'white' : 'secondary'}
                >
                  {offer.offerMetadata.isBackOrder ? (
                    t('app.common.offer_type.backorder')
                  ) : (
                    <FormatDate
                      date={offer.offerMetadata.deliveryDate}
                      format={DateUtils.DATE_FORMAT.DAY_MONTH_ONLY}
                      locale={locale.locale}
                    />
                  )}
                </Text>
                {offer.offerMetadata.isJ0 && (
                  <Icon
                    id='today-delivery-icon'
                    icon={
                      offer.offerMetadata.todayDelivery
                        ? 'today-delivery'
                        : 'next-day-delivery'
                    }
                  />
                )}
                {customer.settingTemplate?.generalSetting.displayBestDelayTag &&
                  offer.offerMetadata.isBestDelay && (
                    <div className='w-100'>
                      <Badge bg='info' className='text-nowrap'>
                        <Text
                          id={`best-delay-${product.tyre.ean}-${supplier.name}-${offerIndex}`}
                          heading='xxs'
                          weight='semi-bold'
                          color='primary'
                        >
                          <Trans i18nKey='app.purchase.best_delay' />
                        </Text>
                      </Badge>
                    </div>
                  )}
              </>
            </div>
          </Flex>
        )}
      </td>
      <td id='stock' className='p-0 no-border' align='right'>
        <Flex
          direction='column'
          alignItems='center'
          justifyContent='center'
          className='list-unstyled h-100 w-100'
          wrap='wrap'
        >
          <div className={'w-100 px-1 justify-content-center'}>
            <>
              {isPartiallyAvailable ? (
                <Badge bg='info'>{offer.offerMetadata.availableQuantity}</Badge>
              ) : (
                <Text heading='sm' weight='semi-bold'>
                  {offer.offerMetadata.availableQuantity}
                </Text>
              )}
            </>
          </div>
        </Flex>
      </td>
      <td id='quantity' className='p-0 no-border'>
        <div className='px-1'>
          <NumberField
            id={`${supplier.name}-${offerIndex}`}
            name={`quantity.${offerIndex}`}
            size='sm'
            min={MIN_QUANTITY}
            max={offer.offerMetadata.availableQuantity}
            disabled={
              offer.offerMetadata.isFallback ||
              !offer.offerMetadata.availableQuantity ||
              cartItems.length > 0 ||
              (!supplier.orderable && !isInRedistribution) ||
              !product.canOrder ||
              !acl.isGranted(
                [ROLES.ROLE_UPDATE_CART, ROLES.ROLE_UPDATE_REDISTRIBUTION_CART],
                'affirmative',
              )
            }
            onChangeValue={onUpdateQuantity(supplier)}
            feedbackType='tooltip'
          />
        </div>
      </td>
      <td id='unit-price' className='p-0 no-border' align='right'>
        <Flex alignItems='center'>
          {offer.offerMetadata.isJ0 && (
            <div className='w-100'>
              <Badge
                bg='tertiary-bold'
                className='text-nowrap'
                id='j0-price-badge'
              >
                {!isNil(offer.offerMetadata.j0Price) && <span>{'+'}</span>}
                {locale.formatters.money(offer.offerMetadata.j0Price, {
                  currency: product.tyre.priceFrame.currencyIsoCode,
                  fallback: '-',
                })}
              </Badge>
            </div>
          )}
          <Flex
            direction='column'
            justifyContent='around'
            className='w-100 h-100 '
            wrap='wrap'
          >
            <div className={'w-100 px-1'}>
              {customer.settingTemplate?.generalSetting.displayBestPriceTag &&
                offer.offerMetadata.isBestPrice && (
                  <div className='w-100'>
                    <Badge bg='info' className='text-nowrap'>
                      <Text heading='xxs' weight='semi-bold' color='primary'>
                        <Trans i18nKey='app.purchase.best_price' />
                      </Text>
                    </Badge>
                  </div>
                )}
              <div className='w-100'>
                <Text heading='sm' weight='semi-bold' id='unit-price-value'>
                  {!isNil(offer.offerMetadata.displayPrice) &&
                  offer.offerMetadata.displayPrice !== 0
                    ? locale.formatters.money(
                        offer.offerMetadata.displayPrice,
                        {
                          currency: product.tyre.priceFrame.currencyIsoCode,
                        },
                      )
                    : '-'}
                </Text>
                {!isNil(product.tyre.priceFrame.price) && (
                  <Text
                    heading='xxs'
                    weight='semi-bold'
                    color='secondary-gray'
                    id='discount-percentage'
                  >
                    {!isNil(offer.offerMetadata.displayPrice) &&
                    offer.offerMetadata.displayPrice !== 0
                      ? t('app.purchase.bf_discount', {
                          price: offer.offerMetadata.discountPercentage,
                        })
                      : '-'}
                  </Text>
                )}
              </div>
            </div>
          </Flex>
        </Flex>
      </td>
      {showCommon && (
        <td id='action' rowSpan={offersCount} className='align-middle p-0 '>
          <Flex
            className='p-1 gap-2'
            direction='column'
            justifyContent='center'
            alignItems='center'
          >
            <>
              {isPartiallyAvailable && (
                <Badge bg='info' data-cy='partial-availability'>
                  <Trans
                    i18nKey='app.purchase.table.total_partial'
                    values={{
                      count: product.totalAvailableQuantity,
                    }}
                  />
                </Badge>
              )}
              {product.containsFallbackOffers && (
                <IconButton
                  id='refetch-tyre'
                  variant='secondary'
                  icon='reload'
                  iconAlign='right'
                  size='xs'
                  isLoading={manageSearch.refreshEan.isLoading}
                  onClick={() => {
                    onRefreshTyre(product.tyre.ean)
                      .then((offers) => {
                        methods.reset({
                          quantity: offers.map(
                            (offer) => offer.offerMetadata.proposedQuantity,
                          ),
                        });
                      })
                      .catch(() => {});
                  }}
                >
                  <span className='add-to-cart-text'>
                    <Trans i18nKey='app.common.button.relaunch' />
                  </span>
                </IconButton>
              )}
              <PrivateComponent
                roles={[
                  ROLES.ROLE_UPDATE_CART,
                  ROLES.ROLE_UPDATE_REDISTRIBUTION_CART,
                ]}
                strategy='affirmative'
              >
                {product.canOrder ? (
                  <PurchaseAddToCartButton
                    id={`${product.tyre.ean}-add-to-cart`}
                    isLoading={isAdding}
                    disabled={
                      !product.canOrder ||
                      tyreOrderedQuantity > 0 ||
                      manageSearch.refreshEan.isLoading
                    }
                    form={product.tyre.ean}
                    productOrderedQuantity={tyreOrderedQuantity}
                  />
                ) : (
                  <Tooltip
                    label={<Trans i18nKey='app.common.status.not_orderable' />}
                  >
                    <PurchaseAddToCartButton
                      form={product.tyre.ean}
                      id={`${product.tyre.ean}-add-to-cart`}
                      isLoading={isAdding}
                      disabled={
                        !product.canOrder ||
                        tyreOrderedQuantity > 0 ||
                        manageSearch.refreshEan.isLoading
                      }
                      productOrderedQuantity={tyreOrderedQuantity}
                    />
                  </Tooltip>
                )}
              </PrivateComponent>
              <PrivateComponent
                roles={[
                  ROLES.ROLE_DELETE_CART,
                  ROLES.ROLE_DELETE_REDISTRIBUTION_CART,
                ]}
                strategy='affirmative'
              >
                {cartItems.length > 0 && (
                  <Button
                    id={`${product.tyre.ean}-remove-from-cart`}
                    variant='link'
                    size='xs'
                    isLoading={cart.remove.isLoading}
                    onClick={() =>
                      cart.remove.mutate(
                        cartItems.map((item) => item.id),
                        {
                          onError: async (error) => {
                            if (
                              error.message ===
                              PurchaseErrorCodes.ERR_INVALID_CART
                            ) {
                              await cart.get.refetch();
                            }
                          },
                        },
                      )
                    }
                  >
                    <span className='me-1'>
                      <Trans i18nKey='app.common.buttons.remove' />
                    </span>
                    <Icon icon='trash' size='xs' />
                  </Button>
                )}
              </PrivateComponent>
            </>
          </Flex>
        </td>
      )}
      {detailOpened && (
        <PurchaseDetailModal
          customer={customer}
          product={product.tyre}
          onClose={detailHandlers.setFalse}
        />
      )}
    </tr>
  );
}

export default OfferSuccessRow;
